import { useState } from 'react';
import { Button } from '@/ui/components/Button';
import { ToggleSwitchCard } from '@/ui/components/ToggleSwitchCard';
import { checkMarkButton } from '@/assets/images/emojis';
import styles from './OfferTermsPage.module.scss';
import { Icon } from '@/ui/icons/Icon';
import { routes } from '@/routes/routes';
import { Link } from 'react-router-dom';
import { useTerms } from '@/features/terms/hooks/useTerms';
import { useNavigate } from 'react-router-dom';
import { useOfferRequest } from '@/features/offer/hooks/useOfferRequest';
import { useLogger } from '@/lib/logger';

interface TermsState {
  selectAll: boolean;
  creditBureau: boolean;
  anaf: boolean;
}

function OfferTermsPage() {
  const navigate = useNavigate();
  const logger = useLogger();
  const { requestOffer } = useOfferRequest();
  const {
    acceptBcTerms,
    acceptPartnerAnafTerms,
    isLoading: isTermsLoading,
  } = useTerms();

  const [acceptedTerms, setAcceptedTerms] = useState<TermsState>({
    selectAll: false,
    creditBureau: false,
    anaf: false,
  });

  const handleSelectAllChange = (checked: boolean) => {
    setAcceptedTerms({
      selectAll: checked,
      creditBureau: checked,
      anaf: checked,
    });
  };

  const handleTermChange = (
    termKey: keyof Omit<TermsState, 'selectAll'>,
    checked: boolean
  ) => {
    const newTerms = {
      ...acceptedTerms,
      [termKey]: checked,
    };

    const allSelected = Object.entries(newTerms)
      .filter(([key]) => key !== 'selectAll')
      .every(([, value]) => value === true);

    newTerms.selectAll = allSelected;

    setAcceptedTerms(newTerms);
  };

  const isContinueDisabled = !acceptedTerms.selectAll || isTermsLoading;

  const handleContinue = async () => {
    if (isContinueDisabled) {
      return;
    }

    try {
      await acceptBcTerms();
      await acceptPartnerAnafTerms();
      await requestOffer();

      navigate(routes.offer.base);
    } catch (error) {
      logger.error('Failed to accept terms', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        {/* Header Section */}
        <div className={styles.header}>
          <div className={styles.emojiContainer}>
            <img
              src={checkMarkButton}
              alt="Confirm agreement"
              className={styles.emoji}
            />
          </div>
          <div className={styles.textContainer}>
            <h1 className={styles.title}>Confirmă acordul tău</h1>
            <p className={styles.subtitle}>
              Selectează opțiunile de mai jos pentru a continua.
            </p>
          </div>
        </div>

        {/* Terms Section */}
        <div className={styles.termsSection}>
          {/* Select All Card */}
          <ToggleSwitchCard
            isSelectAll
            checked={acceptedTerms.selectAll}
            label="Selectați tot"
            onChange={handleSelectAllChange}
            testId="select-all"
            className={styles.selectAllCard}
          />

          {/* Credit Bureau */}
          <div className={styles.termsItem}>
            <ToggleSwitchCard
              checked={acceptedTerms.creditBureau}
              label="Sunt de acord cu interogarea la Biroul de credit"
              onChange={(checked) => handleTermChange('creditBureau', checked)}
              testId="credit-bureau"
            />
            <div className={styles.linkContainer}>
              <Link to={routes.terms.bc} className={styles.link}>
                Termeni și condiții Biroul de Credit
              </Link>
            </div>
          </div>

          {/* ANAF */}
          <div className={styles.termsItem}>
            <ToggleSwitchCard
              checked={acceptedTerms.anaf}
              label="Sunt de acord cu interogarea la ANAF"
              onChange={(checked) => handleTermChange('anaf', checked)}
              testId="anaf"
            />
            <div className={styles.linkContainer}>
              <Link to={routes.terms.anaf} className={styles.link}>
                Termeni și condiții ANAF
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <Button
          trackingId="accept_offer_terms"
          variant="filled"
          text="Continuă"
          className={styles.button}
          onClick={handleContinue}
          disabled={isContinueDisabled}
          loading={isTermsLoading}
          rightIcon={<Icon name="arrow-right" />}
        />
      </div>
    </div>
  );
}

export default OfferTermsPage;
